import React, { MouseEvent, useState } from 'react';
import { PatientWithAccessRightDTO } from '@peartherapeutics/weaver-api-client';
import { withStyles } from '@material-ui/styles';
import { Dialog, DialogContent, Select, MenuItem } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import ButtonTable from 'components/common/Buttons/ButtonTable';
import { designConstants as design } from 'styles/designConstants';
import { formatDate } from 'utils/dateTime';

import './styles.scss';

export interface IReRxDialogProps {
  classes: any;
  children?: JSX.Element;
  closeDialog: (event: MouseEvent) => void;
  headerTitle: String;
  headerStyling: any;
  keepMounted: boolean;
  openDialog: boolean;
  onClick: (event: MouseEvent) => void;
  onClose: (event: MouseEvent) => void;
  patient: PatientWithAccessRightDTO;
}

export interface IReRxButtonProps {
  classes: any;
  patient: PatientWithAccessRightDTO;
  updatePatientData: (patientData: PatientWithAccessRightDTO) => void;
}

const customStyle = () => ({
  dialog: {
    '& .MuiDialog-paper': {
      color: design.color.$black_full,
      maxHeight: '80vh',
      minHeight: '410px',
      padding: '20px',
      maxWidth: '80vw',
      minWidth: '482px',
      wordBreak: 'break-word',
    },
  },
  content: {
    padding: '10px',
    color: design.color.$black_full,
    fontSize: '17px',
    lineHeight: '1.8',
  },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridColumn: 2,
  },
  formHeader: {
    padding: '10px',
    fontSize: '24px',
    fontWeight: 700,
  },
  confirmTitle: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '30px',
    gridColumn: 1,
    width: 'auto',
  },
  successTitle: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    fontSize: '21px',
    fontWeight: 600,
    lineHeight: '30px',
    gridColumn: 1,
    width: 'auto',
  },
  container: {
    marginTop: '10px',
  },
  btn: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
  },
  accessCodeText: {
    fontSize: '20px',
    alignSelf: 'center',
    padding: '10px',
  },
});

const ReRxDialog = (props: IReRxDialogProps) => {
  const {
    classes,
    children,
    closeDialog,
    headerTitle,
    headerStyling,
    keepMounted,
    openDialog,
    onClick,
    onClose,
    patient,
  } = props;

  return (
    <Dialog
      className={classes.dialog}
      open={openDialog}
      keepMounted={keepMounted}
      onClose={onClose}
      onClick={onClick}
    >
      <div className={classes.formHeader} styleName="form-header">
        <div className={headerStyling}>{headerTitle}</div>
        <span className={classes.closeButton}>
          <IconButton aria-label="close" edge="end" onClick={closeDialog}>
            <CloseIcon />
          </IconButton>
        </span>
      </div>

      <DialogContent dividers className={classes.content}>
        <div className={classes.container}>
          <div>
            First Name: <strong>{patient.firstName}</strong>
          </div>
          <div>
            Last Name: <strong>{patient.lastName}</strong>
          </div>
          <div>
            DOB: <strong>{formatDate(patient.birthDate) || 'N/A'}</strong>
          </div>
          <div>
            Email: <strong>{patient.email}</strong>
          </div>
          <div>
            Product: <strong>{patient.currentAccessRight!.program}</strong>
          </div>
          <div>
            Fill Count: <strong>{patient.accessRightCount}</strong>
          </div>
          <div>
            <label>Select Configuration: </label>
            <Select value={props.selectedConfig} onChange={props.handleConfigChange}>
              <MenuItem value="NonMonetary">NonMonetary</MenuItem>
              <MenuItem value="CM75">CM75</MenuItem>
              <MenuItem value="CM150">CM150</MenuItem>
              <MenuItem value="CM500">CM500</MenuItem>
            </Select>
          </div>
        </div>
      </DialogContent>
      {children}
    </Dialog>
  );
};

const ReRxButton = (props: IReRxButtonProps) => {
  const [isOpenReRxModal, setIsOpenReRxModal] = useState<boolean>(false);
  const [isSuccessReRxModal, setIsSuccessReRxModal] = useState<boolean>(false);
  const [accessCode, setAccessCode] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState('CM150');
  const { patient, updatePatientData, classes } = props;

  const handleConfigChange = (event) => {
    setSelectedConfig(event.target.value);
  };

  const closeConfirmReRxDialog = (event: MouseEvent) => {
    setIsOpenReRxModal(false);
    event.stopPropagation();
  };

  const openConfirmReRxDialog = () => {
    setIsOpenReRxModal(true);
  };

  const closeSuccessReRxDialog = async (event: MouseEvent) => {
    const [updatedPatientData, ,]: PatientWithAccessRightDTO[] =
      await WeaverApiClientInstance.getPatients();

    updatePatientData({
      ...patient,
      ...updatedPatientData,
      currentAccessRight: {
        ...patient.currentAccessRight!,
        ...updatedPatientData.currentAccessRight!,
        program: patient.currentAccessRight!.program,
      },
    });
    setIsSuccessReRxModal(false);
    event.stopPropagation();
  };

  const submitReRxDialog = async () => {
    const { program, id: prevAccessRightId } = patient.currentAccessRight!;
    const { accessCode } = await WeaverApiClientInstance.rePrescribe({
      userId: patient.userId!,
      program,
      prevAccessRightId,
      configuration: { reward_policy: { name: selectedConfig } },
      prescribingHcpId: patient.healthCareProvider?.id,
    });
    setAccessCode(accessCode);
    setIsOpenReRxModal(false);
    setIsSuccessReRxModal(true);
  };

  const handlePropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <span onClick={handlePropagation}>
      <ButtonTable
        aria-label="represcription"
        buttonRole={'dashboard'}
        handleClick={openConfirmReRxDialog}
      >
        Re-Rx
      </ButtonTable>
      <ReRxDialog
        openDialog={isOpenReRxModal}
        keepMounted
        onClose={closeConfirmReRxDialog}
        onClick={handlePropagation}
        headerTitle="Confirm re-prescription details"
        closeDialog={closeConfirmReRxDialog}
        patient={patient}
        classes={classes}
        headerStyling={classes.confirmTitle}
        selectedConfig={selectedConfig}
        handleConfigChange={handleConfigChange}
      >
        <div className={classes.btn}>
          <ButtonMedium
            data-testid="cancel-btn"
            aria-label="cancel"
            buttonRole={'secondary'}
            width={'147px'}
            margin={'10px 10px 0 0'}
            handleClick={closeConfirmReRxDialog}
          >
            CANCEL
          </ButtonMedium>
          <ButtonMedium
            aria-label="submit"
            buttonRole={'primary'}
            width={'147px'}
            margin={'10px 10px 0 0'}
            handleClick={submitReRxDialog}
          >
            SUBMIT RE-RX
          </ButtonMedium>
        </div>
      </ReRxDialog>
      <ReRxDialog
        openDialog={isSuccessReRxModal}
        keepMounted
        onClose={closeSuccessReRxDialog}
        onClick={handlePropagation}
        headerTitle="Re-prescription successfully created"
        closeDialog={closeSuccessReRxDialog}
        patient={patient}
        classes={classes}
        headerStyling={classes.successTitle}
      >
        <div className={classes.accessCodeText}>
          Access code: <strong>{accessCode}</strong>
        </div>
      </ReRxDialog>
    </span>
  );
};

export default withStyles(customStyle)(ReRxButton);
